<template>
  <transition name="fade">
    <div v-if="visible" class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div
          :class="backdropClass"
          aria-hidden="true"
          class="absolute inset-0"
          @click="handleClose"
      ></div>
      <!-- A flex column, not one long scrolling box. The panel used to scroll
           as a whole, so any footer scrolled away with the content and anything
           pinned inside the body had to fake a footer with `sticky`, which read
           as a second panel floating inside the first. One scroll region, one
           footer that is genuinely part of the panel. -->
      <div
          ref="panel"
          :aria-labelledby="$slots.header ? headerId : undefined"
          :class="panelClassComputed"
          :style="{ maxHeight: panelMaxHeight }"
          aria-modal="true"
          class="relative flex w-full flex-col text-gray-800 dark:text-gray-100 overflow-hidden focus:outline-none"
          role="dialog"
          tabindex="-1"
          @keydown="onPanelKeydown"
      >
        <button
            :aria-label="$t('close')"
            :class="closeButtonClass"
            :disabled="isActionLoading"
            class="absolute top-2.5 right-2.5 z-[110] flex h-11 w-11 items-center justify-center rounded-full transition-colors"
            type="button"
            @click="handleClose"
        >
          <XIcon aria-hidden="true" class="h-5 w-5"/>
        </button>
        <div :class="bodyClassComputed">
          <header
              v-if="$slots.header"
              :id="headerId"
              :class="[
                variant === 'sheet' ? 'pr-10' : 'pr-8',
                variant === 'sheet' ? (bodyFill ? 'mb-4' : 'mb-1') : 'mb-2',
                bodyFill ? 'shrink-0' : '',
              ]"
          >
            <slot name="header"></slot>
          </header>
          <section :class="bodyFill ? 'flex min-h-0 flex-1 flex-col' : 'mb-6'">
            <slot></slot>
          </section>
        </div>
        <footer
            v-if="(dangerText.length || cancelText.length || confirmText.length) || (variant === 'sheet' && $slots.footerNote)"
            :class="footerClassComputed"
        >
          <div :class="variant === 'sheet' ? 'flex w-full flex-col gap-2.5' : 'contents'">
            <button
                v-if="dangerText.length"
                :class="dangerButtonClass"
                :aria-busy="dangerLoading"
                :disabled="dangerLoading"
                type="button"
                @click="handleDanger"
            >
              <LoaderCircleIcon v-if="dangerLoading" class="h-4 w-4 shrink-0 animate-spin" aria-hidden="true"/>
              <span>{{ dangerText }}</span>
            </button>
            <button
                v-if="cancelText.length"
                :class="cancelButtonClass"
                type="button"
                @click="handleCancel"
            >
              {{ cancelText }}
            </button>
            <button
                v-if="confirmText.length"
                :class="confirmButtonClass"
                :aria-busy="confirmLoading"
                :disabled="confirmDisabled || confirmLoading"
                type="button"
                @click="handleConfirm"
            >
              <LoaderCircleIcon v-if="confirmLoading" class="h-4 w-4 shrink-0 animate-spin" aria-hidden="true"/>
              <span>{{ confirmText }}</span>
            </button>
            <div
                v-if="$slots.footerNote && variant === 'sheet'"
                class="text-start pt-2.5"
            >
              <slot name="footerNote"></slot>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { LoaderCircle as LoaderCircleIcon, X as XIcon } from 'lucide-vue-next'

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
let nextModalId = 0

export default {
  name: 'BaseModal', components: {
    LoaderCircleIcon,
    XIcon
  }, props: {
    visible: {
      type: Boolean, default: false,
    }, confirmText: {
      type: String, default: 'Ok',
    }, cancelText: {
      type: String, default: '',
    }, dangerText: {
      type: String, default: '',
    }, confirmDisabled: {
      type: Boolean, default: false,
    }, confirmLoading: {
      type: Boolean, default: false,
    }, dangerLoading: {
      type: Boolean, default: false,
    }, closeOnConfirm: {
      type: Boolean, default: true,
    }, closeOnCancel: {
      type: Boolean, default: true,
    }, closeOnDanger: {
      type: Boolean, default: true,
    },
    /**
     * default: classic compact modal (existing behaviour).
     * sheet: iOS-style card (blur backdrop, large radius, stacked full-width actions).
     */
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'sheet'].includes(v),
    },
    /**
     * Hands the height to the default slot instead of scrolling around it.
     *
     * For content that owns its own scroll region and footer, such as the
     * upgrade picker: without this the modal scrolls the picker, the picker
     * scrolls itself, and the two read as boxes nested inside each other.
     */
    bodyFill: {
      type: Boolean, default: false,
    },
    /**
     * default variant only: wide = responsive lg panel (recipe filters / planner search).
     */
    panelSize: {
      type: String,
      default: 'md',
      validator: (v) => ['md', 'wide'].includes(v),
    },
    /**
     * default variant only: frosted panel + blur backdrop (sheet-like chrome at max-w-md).
     */
    polished: {
      type: Boolean,
      default: false,
    },
  }, emits: ['update:visible', 'confirm', 'cancel', 'danger'], data () {
    return {
      headerId: `base-modal-title-${++nextModalId}`,
      returnFocusTo: null,
    }
  }, computed: {
    isActionLoading () {
      return this.confirmLoading || this.dangerLoading
    },
    defaultUsesPolishedChrome () {
      return this.variant === 'default' && (this.polished || this.panelSize === 'wide')
    },
    panelMaxHeight () {
      if (this.bodyFill) return 'min(92vh, 780px)'
      // A sheet takes the whole screen less the container's padding. At 85vh a
      // 360x640 phone lost 96px to the backdrop, which is what pushed the
      // upgrade sheet's trial button below its own fold. dvh tracks the
      // visible area under mobile browser toolbars. Responsive sheet heights
      // live in CSS so they can match the container's p-4 and sm:p-6 padding.
      if (this.variant === 'sheet') return 'var(--base-modal-sheet-max-height)'
      return 'min(85vh, 720px)'
    },
    backdropClass () {
      if (this.variant === 'sheet' || this.defaultUsesPolishedChrome) {
        return 'bg-black/40 backdrop-blur-md'
      }
      return 'bg-black/50'
    },
    bodyClassComputed () {
      const base = ['flex', 'min-h-0', 'flex-1', 'flex-col']

      if (this.variant === 'sheet') {
        base.push('text-start', 'px-6', 'pt-8')
        // No bottom padding in fill mode: the slot's own footer sits flush
        // against the panel edge, which is what stops it looking like an
        // inner card.
        base.push(this.bodyFill ? '' : 'pb-1')
      } else {
        base.push('p-6')
      }

      if (!this.bodyFill) {
        base.push('overflow-y-auto', 'overscroll-contain')
      }

      return base.filter(Boolean)
    },
    panelClassComputed () {
      if (this.variant === 'sheet') {
        return [
          'base-modal-sheet-panel',
          'w-full max-w-[min(calc(100vw-2rem),22.5rem)] sm:max-w-[min(calc(100vw-2.5rem),24rem)]',
          'md:max-w-[min(calc(100vw-3rem),28rem)] lg:max-w-[min(calc(100vw-3rem),32rem)] xl:max-w-[min(calc(100vw-4rem),36rem)]',
          'mx-auto rounded-[1.35rem]',
          'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl',
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]',
          'ring-1 ring-black/[0.06] dark:ring-white/10',
        ].join(' ')
      }
      if (this.variant === 'default' && this.panelSize === 'wide') {
        return [
          'w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-4 sm:mx-auto rounded-3xl',
          'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl',
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]',
          'ring-1 ring-black/[0.06] dark:ring-white/10',
        ].join(' ')
      }
      if (this.variant === 'default' && this.polished) {
        return [
          'max-w-md mx-4 rounded-[1.25rem]',
          'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl',
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]',
          'ring-1 ring-black/[0.06] dark:ring-white/10',
        ].join(' ')
      }
      return [
        'max-w-md mx-4 rounded-xl',
        'bg-white dark:bg-gray-800 shadow-lg',
      ].join(' ')
    },
    closeButtonClass () {
      if (this.variant === 'sheet' || this.defaultUsesPolishedChrome) {
        return 'text-gray-400 hover:text-gray-600 hover:bg-black/[0.05] dark:hover:bg-white/[0.08] dark:text-gray-500 dark:hover:text-gray-300'
      }
      return 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    },
    footerClassComputed () {
      // shrink-0 on both: the panel is a flex column now, so without it a long
      // body could squeeze the actions rather than scrolling.
      if (this.variant === 'sheet') {
        return [
          'shrink-0 flex flex-col gap-0 px-4 pb-5 pt-3',
          'border-t border-gray-200/80 dark:border-gray-700/80',
        ].join(' ')
      }
      return 'shrink-0 flex justify-end space-x-2 px-6 pb-6'
    },
    dangerButtonClass () {
      if (this.variant === 'sheet') {
        return 'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 py-3.5 text-[15px] font-semibold text-white shadow-sm transition-transform hover:bg-red-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100'
      }
      return 'inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-white transition duration-200 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-600 dark:hover:bg-red-700'
    },
    cancelButtonClass () {
      if (this.variant === 'sheet') {
        return 'w-full py-3.5 rounded-xl text-[15px] font-semibold bg-gray-100 hover:bg-gray-200/90 active:bg-gray-200 text-gray-900 dark:bg-gray-700/90 dark:hover:bg-gray-600 dark:text-gray-100 active:scale-[0.99] transition-transform'
      }
      return 'px-4 py-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-100 rounded-md transition duration-200'
    },
    confirmButtonClass () {
      if (this.variant === 'sheet') {
        return 'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-linear-to-r from-indigo-600 to-violet-600 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-500/30 transition-transform hover:from-indigo-500 hover:to-violet-500 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100'
      }
      return 'inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-white transition duration-200 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-600 dark:hover:bg-emerald-700'
    },
  }, watch: {
    // Dialog focus: move into the panel on open so Escape and screen readers
    // land in the modal, not behind it, and hand focus back on close.
    visible: {
      immediate: true,
      handler (open) {
        if (open) {
          this.returnFocusTo = document.activeElement
          this.$nextTick(() => this.$refs.panel?.focus({ preventScroll: true }))
        } else {
          this.restoreFocus()
        }
      },
    },
  }, beforeUnmount () {
    if (this.visible) this.restoreFocus()
  }, methods: {
    restoreFocus () {
      const el = this.returnFocusTo
      this.returnFocusTo = null
      if (el && el.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true })
    },
    onPanelKeydown (e) {
      if (e.key === 'Escape') {
        // Stop here so a modal opened from inside another only closes itself.
        e.preventDefault()
        e.stopPropagation()
        this.handleClose()
        return
      }
      if (e.key !== 'Tab') return
      e.stopPropagation()
      const list = [...this.$refs.panel.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null)
      if (!list.length) return
      const first = list[0]
      const last = list[list.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || !list.includes(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !list.includes(active))) {
        e.preventDefault()
        first.focus()
      }
    },
    handleClose () {
      if (this.isActionLoading) return
      this.$emit('update:visible', false)
    },
    handleConfirm () {
      if (this.confirmDisabled || this.confirmLoading) return
      this.$emit('confirm')
      if (this.closeOnConfirm) {
        this.$emit('update:visible', false)
      }
    }, handleCancel () {
      if (this.isActionLoading) return
      this.$emit('cancel')
      if (this.closeOnCancel) {
        this.$emit('update:visible', false)
      }
    }, handleDanger () {
      if (this.dangerLoading) return
      this.$emit('danger')
      if (this.closeOnDanger) {
        this.$emit('update:visible', false)
      }
    }
  },
}
</script>

<style scoped>
.base-modal-sheet-panel {
  --base-modal-sheet-max-height: min(85vh, calc(100vh - 2rem), 720px);
  --base-modal-sheet-max-height: min(calc(100dvh - 2rem), 720px);
}

@media (min-width: 640px) {
  .base-modal-sheet-panel {
    --base-modal-sheet-max-height: min(85vh, calc(100vh - 3rem), 720px);
    --base-modal-sheet-max-height: min(calc(100dvh - 3rem), 720px);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
